import React, { useState, useEffect, useMemo } from 'react';
import { formatCurrency } from '../lib/format';
import { useNavigate } from 'react-router-dom';
import { useUser } from '../store/useAuthStore';
import { apiClient } from '../lib/api';
import { useNotifications } from '../hooks/useNotifications';
import {
  DollarSign,
  AlertTriangle,
  Clock,
  Search,
  Filter,
  ChevronDown,
  ChevronUp,
  Users,
  TrendingDown,
  X,
  Send,
  CheckCircle,
  Eye
} from 'lucide-react';
import { formatShortDate } from '../utils/dateUtils';

type Role = 'distributor' | 'supervisor' | 'physician';

interface Aging {
  b0_30: number;
  b31_60: number;
  b61_90: number;
  b91_120: number;
  b121_150: number;
  b151_180: number;
}

interface APRow {
  recipientId: string;
  recipientName: string;
  recipientEmail: string;
  recipientCompany: string | null;
  recipientRole: Role;
  amount: number;
  orderCount: number;
  invoiceCount: number;
  membershipEarnings: number;
  hasOverlappingPayout: boolean;
  oldestUnpaidAt: string | null;
  aging: Aging;
}

interface RecentPayout {
  _id: string;
  recipientId: {
    _id: string;
    profile: { firstName: string; lastName: string };
    distributorData?: { companyName?: string };
    supervisorData?: { companyName?: string };
    role?: string;
    email: string;
  };
  startDate: string;
  endDate: string;
  amount: number;
  status: 'pending' | 'sent' | 'confirmed' | 'cancelled';
  wireNumber?: string;
  createdAt: string;
}

const BUCKETS: Array<{ key: string; label: string; min: number; max: number | null }> = [
  { key: 'b0_30',   label: '0-30',    min: 0,   max: 30 },
  { key: 'b31_60',  label: '31-60',   min: 31,  max: 60 },
  { key: 'b61_90',  label: '61-90',   min: 61,  max: 90 },
  { key: 'b91_120', label: '91-120',  min: 91,  max: 120 },
  { key: 'b121_150',label: '121-150', min: 121, max: 150 },
  { key: 'b151_180',label: '151-180', min: 151, max: 180 }
];

const BUCKET_COLORS: Record<string, { text: string; chip: string }> = {
  b0_30:    { text: 'text-blue-600',    chip: 'bg-blue-50 text-blue-700' },
  b31_60:   { text: 'text-yellow-600',  chip: 'bg-yellow-50 text-yellow-700' },
  b61_90:   { text: 'text-orange-600',  chip: 'bg-orange-50 text-orange-700' },
  b91_120:  { text: 'text-red-600',     chip: 'bg-red-50 text-red-700' },
  b121_150: { text: 'text-red-700',     chip: 'bg-red-100 text-red-800' },
  b151_180: { text: 'text-red-800',     chip: 'bg-red-200 text-red-900' }
};

const roleLabel = (role: Role) =>
  role === 'distributor' ? 'AM' : role === 'supervisor' ? 'Supervisor' : 'Physician';

function daysSince(date: string | null, now: Date): number | null {
  if (!date) return null;
  return Math.floor((now.getTime() - new Date(date).getTime()) / (1000 * 60 * 60 * 24));
}

export const AdminAP: React.FC = () => {
  const user = useUser();
  const navigate = useNavigate();
  const { notifyError, notifySuccess } = useNotifications();

  const now = useMemo(() => new Date(), []);
  // Trailing 6 months (180 days)
  const [windowStart] = useState<string>(() => {
    const d = new Date();
    d.setDate(d.getDate() - 180);
    return d.toISOString().split('T')[0];
  });
  const [windowEnd] = useState<string>(() => new Date().toISOString().split('T')[0]);

  const [loading, setLoading] = useState(true);
  const [rows, setRows] = useState<APRow[]>([]);
  const [recentPayouts, setRecentPayouts] = useState<RecentPayout[]>([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [roleFilter, setRoleFilter] = useState<'all' | Role>('all');
  const [overdueOnly, setOverdueOnly] = useState(false);
  const [sortField, setSortField] = useState<'amount' | 'oldest' | 'name'>('amount');
  const [sortDir, setSortDir] = useState<'asc' | 'desc'>('desc');

  const [selectedRow, setSelectedRow] = useState<APRow | null>(null);

  useEffect(() => {
    if (user?.role === 'admin') {
      loadAP();
      loadRecentPayouts();
    }
  }, [user]);

  const loadAP = async () => {
    setLoading(true);
    try {
      const res = await apiClient.getBulkPayoutPreview({ startDate: windowStart, endDate: windowEnd });
      if (res.success && res.data) {
        setRows(res.data.rows || []);
      } else {
        notifyError('Failed to load', res.error || 'Could not load AP data');
      }
    } catch (err) {
      console.error(err);
      notifyError('Error', 'Failed to load accounts payable');
    } finally {
      setLoading(false);
    }
  };

  const loadRecentPayouts = async () => {
    try {
      const res = await apiClient.getPayouts();
      if (res.success && res.data) {
        const cutoff = new Date();
        cutoff.setDate(cutoff.getDate() - 30);
        const recent = (res.data as RecentPayout[])
          .filter(p => p.status !== 'cancelled' && new Date(p.createdAt) >= cutoff)
          .sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime());
        setRecentPayouts(recent);
      }
    } catch (err) {
      console.error(err);
    }
  };

  const markSent = async (payoutId: string) => {
    const res = await apiClient.markPayoutAsSent(payoutId);
    if (res.success) {
      notifySuccess('Payout marked sent', 'Recipient can now confirm receipt');
      loadRecentPayouts();
    } else {
      notifyError('Failed', res.error || 'Could not mark as sent');
    }
  };

  const toggleSort = (field: typeof sortField) => {
    if (sortField === field) setSortDir(sortDir === 'asc' ? 'desc' : 'asc');
    else { setSortField(field); setSortDir('desc'); }
  };

  // Bucket totals across all rows (sum each recipient's aging breakdown)
  const totals = useMemo(() => {
    const buckets: Record<string, number> = {};
    for (const b of BUCKETS) buckets[b.key] = 0;
    let total = 0;
    let recipientCount = 0;
    let overdueRecipientCount = 0;
    for (const row of rows) {
      for (const b of BUCKETS) {
        buckets[b.key] += row.aging[b.key as keyof Aging] || 0;
      }
      total += row.amount;
      recipientCount++;
      const overdueAmt = (row.aging.b61_90 || 0) + (row.aging.b91_120 || 0)
        + (row.aging.b121_150 || 0) + (row.aging.b151_180 || 0);
      if (overdueAmt > 0) overdueRecipientCount++;
    }
    return { buckets, total, recipientCount, overdueRecipientCount };
  }, [rows]);

  const filteredRows = useMemo(() => {
    return rows.filter(r => {
      if (roleFilter !== 'all' && r.recipientRole !== roleFilter) return false;
      if (overdueOnly) {
        const days = daysSince(r.oldestUnpaidAt, now);
        if (days === null || days < 60) return false;
      }
      if (searchTerm) {
        const s = searchTerm.toLowerCase();
        if (
          !r.recipientName.toLowerCase().includes(s) &&
          !r.recipientEmail.toLowerCase().includes(s) &&
          !(r.recipientCompany || '').toLowerCase().includes(s)
        ) return false;
      }
      return true;
    });
  }, [rows, roleFilter, overdueOnly, searchTerm, now]);

  const sortedRows = useMemo(() => {
    const arr = [...filteredRows];
    arr.sort((a, b) => {
      let cmp = 0;
      if (sortField === 'amount') cmp = a.amount - b.amount;
      else if (sortField === 'oldest') {
        const ad = a.oldestUnpaidAt ? new Date(a.oldestUnpaidAt).getTime() : Infinity;
        const bd = b.oldestUnpaidAt ? new Date(b.oldestUnpaidAt).getTime() : Infinity;
        // Oldest first when desc
        cmp = bd - ad;
      } else if (sortField === 'name') cmp = a.recipientName.localeCompare(b.recipientName);
      return sortDir === 'desc' ? -cmp : cmp;
    });
    return arr;
  }, [filteredRows, sortField, sortDir]);

  if (!user || user.role !== 'admin') {
    return (
      <div className="min-h-screen bg-gradient-to-br from-red-50 to-orange-100 flex items-center justify-center">
        <div className="text-center">
          <AlertTriangle className="mx-auto h-12 w-12 text-red-400" />
          <h3 className="mt-2 text-sm font-medium text-gray-900">Access Denied</h3>
          <p className="mt-1 text-sm text-gray-500">Admin access required.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-gray-900">Accounts Payable</h1>
          <p className="text-gray-600 mt-2">
            What we owe to AMs, supervisors, and physicians — earned but not yet paid out (trailing 6 months)
          </p>
        </div>

        {/* Summary cards: 6 buckets + total */}
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-7 gap-4 mb-8">
          {BUCKETS.map((b) => (
            <div key={b.key} className="bg-white rounded-lg shadow p-4">
              <div className="flex items-center">
                {b.key === 'b0_30' ? <DollarSign className="h-8 w-8 text-blue-600" />
                  : b.key.startsWith('b31') ? <Clock className="h-8 w-8 text-yellow-500" />
                  : b.key.startsWith('b61') ? <Clock className="h-8 w-8 text-orange-500" />
                  : <AlertTriangle className={`h-8 w-8 ${BUCKET_COLORS[b.key].text}`} />}
                <div className="ml-3">
                  <p className="text-xs font-medium text-gray-500">{b.label} Days</p>
                  <p className={`text-lg font-bold ${BUCKET_COLORS[b.key].text}`}>
                    {formatCurrency(totals.buckets[b.key] || 0)}
                  </p>
                </div>
              </div>
            </div>
          ))}

          <div className="bg-white rounded-lg shadow p-4">
            <div className="flex items-center">
              <TrendingDown className="h-8 w-8 text-indigo-600" />
              <div className="ml-3">
                <p className="text-xs font-medium text-gray-500">Total AP</p>
                <p className="text-lg font-bold text-indigo-600">{formatCurrency(totals.total)}</p>
              </div>
            </div>
          </div>
        </div>

        {/* Stats row */}
        <div className="flex items-center gap-6 mb-6 text-sm text-gray-600">
          <div className="flex items-center gap-2">
            <Users className="h-4 w-4" />
            <span>{totals.recipientCount} recipients owed</span>
          </div>
          <div className="flex items-center gap-2 text-red-600">
            <AlertTriangle className="h-4 w-4" />
            <span>{totals.overdueRecipientCount} with balances older than 60 days</span>
          </div>
        </div>

        {/* Filters */}
        <div className="bg-white rounded-lg shadow p-4 mb-6">
          <div className="flex flex-wrap items-center gap-4">
            <div className="relative flex-1 min-w-[200px]">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
              <input
                type="text"
                placeholder="Search recipients..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            </div>

            <div className="flex items-center gap-2">
              <Filter className="h-4 w-4 text-gray-400" />
              <select
                value={roleFilter}
                onChange={(e) => setRoleFilter(e.target.value as any)}
                className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
              >
                <option value="all">All Roles</option>
                <option value="distributor">AMs</option>
                <option value="supervisor">Supervisors</option>
                <option value="physician">Physicians</option>
              </select>
            </div>

            <label className="flex items-center gap-2 cursor-pointer">
              <input
                type="checkbox"
                checked={overdueOnly}
                onChange={(e) => setOverdueOnly(e.target.checked)}
                className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
              />
              <span className="text-sm text-gray-600">60+ days only</span>
            </label>

            <button
              onClick={() => navigate('/admin/payouts')}
              className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
            >
              Draft Payouts →
            </button>
          </div>
        </div>

        {loading ? (
          <div className="bg-white rounded-lg shadow p-8 text-center">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto"></div>
            <p className="mt-2 text-gray-500">Loading...</p>
          </div>
        ) : (
          <div className="space-y-8">
            {/* Section 1: Currently Owed */}
            <div className="bg-white rounded-lg shadow overflow-hidden">
              <div className="px-6 py-4 bg-orange-50 border-b border-orange-200">
                <h2 className="text-lg font-semibold text-orange-800 flex items-center gap-2">
                  <DollarSign className="h-5 w-5" />
                  Currently Owed ({sortedRows.length})
                </h2>
                <p className="text-sm text-orange-600 mt-1">
                  Recipients with earned revenue not yet covered by a payout — aging by oldest unpaid accrual
                </p>
              </div>
              {sortedRows.length === 0 ? (
                <div className="p-8 text-center text-gray-500">No outstanding balances</div>
              ) : (
                <div className="data-table-container">
                  <div className="data-table-scroll">
                    <table className="data-table">
                      <thead>
                        <tr>
                          <th className="cursor-pointer" onClick={() => toggleSort('name')}>
                            <div className="flex items-center gap-1">
                              Recipient
                              {sortField === 'name' && (sortDir === 'desc' ? <ChevronDown className="h-3 w-3" /> : <ChevronUp className="h-3 w-3" />)}
                            </div>
                          </th>
                          <th>Role</th>
                          <th className="cursor-pointer text-right" onClick={() => toggleSort('amount')}>
                            <div className="flex items-center gap-1 justify-end">
                              Owed
                              {sortField === 'amount' && (sortDir === 'desc' ? <ChevronDown className="h-3 w-3" /> : <ChevronUp className="h-3 w-3" />)}
                            </div>
                          </th>
                          {BUCKETS.map(b => (
                            <th key={b.key} className="text-right">{b.label}d</th>
                          ))}
                          <th className="cursor-pointer text-right" onClick={() => toggleSort('oldest')}>
                            <div className="flex items-center gap-1 justify-end">
                              From
                              {sortField === 'oldest' && (sortDir === 'desc' ? <ChevronDown className="h-3 w-3" /> : <ChevronUp className="h-3 w-3" />)}
                            </div>
                          </th>
                          <th className="text-center">Notes</th>
                        </tr>
                      </thead>
                      <tbody>
                        {sortedRows.map((row) => {
                          const days = daysSince(row.oldestUnpaidAt, now);
                          const hasOld = (row.aging.b91_120 + row.aging.b121_150 + row.aging.b151_180) > 0;
                          const hasMidOld = row.aging.b61_90 > 0;
                          return (
                            <tr
                              key={row.recipientId}
                              className={`cursor-pointer hover:bg-gray-50 ${hasOld ? 'bg-red-50' : hasMidOld ? 'bg-orange-50' : ''}`}
                              onClick={() => setSelectedRow(row)}
                            >
                              <td className="cell-primary" style={{ maxWidth: '220px' }}>
                                <div className="cell-stack">
                                  <span className="cell-stack-value break-words">{row.recipientName}</span>
                                  {row.recipientCompany && (
                                    <span className="cell-stack-label break-words">{row.recipientCompany}</span>
                                  )}
                                  <span className="cell-stack-label cell-truncate">{row.recipientEmail}</span>
                                </div>
                              </td>
                              <td className="cell-secondary">
                                <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-gray-100 text-gray-700">
                                  {roleLabel(row.recipientRole)}
                                </span>
                              </td>
                              <td className="cell-mono text-right font-semibold">
                                {formatCurrency(row.amount)}
                              </td>
                              {BUCKETS.map(b => {
                                const v = row.aging[b.key as keyof Aging] || 0;
                                return (
                                  <td key={b.key} className={`cell-mono text-right ${v > 0 ? BUCKET_COLORS[b.key].text : 'text-gray-300'}`}>
                                    {v > 0 ? formatCurrency(v) : '-'}
                                  </td>
                                );
                              })}
                              <td className="cell-mono text-right text-xs">
                                {row.oldestUnpaidAt ? (
                                  <>
                                    {formatShortDate(row.oldestUnpaidAt)}
                                    {days !== null && <div className="text-gray-400">{days}d ago</div>}
                                  </>
                                ) : '-'}
                              </td>
                              <td className="text-xs text-gray-600">
                                {row.hasOverlappingPayout && (
                                  <span className="text-amber-700">⚠ overlap</span>
                                )}
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                      <tfoot>
                        <tr className="bg-gray-50 font-semibold">
                          <td colSpan={2} className="text-right">Total</td>
                          <td className="cell-mono text-right text-indigo-700">
                            {formatCurrency(sortedRows.reduce((s, r) => s + r.amount, 0))}
                          </td>
                          {BUCKETS.map(b => (
                            <td key={b.key} className={`cell-mono text-right ${BUCKET_COLORS[b.key].text}`}>
                              {formatCurrency(sortedRows.reduce((s, r) => s + (r.aging[b.key as keyof Aging] || 0), 0))}
                            </td>
                          ))}
                          <td colSpan={2}></td>
                        </tr>
                      </tfoot>
                    </table>
                  </div>
                </div>
              )}
            </div>

            {/* Section 2: Recently Paid (Last 30d) */}
            <div className="bg-white rounded-lg shadow overflow-hidden">
              <div className="px-6 py-4 bg-green-50 border-b border-green-200">
                <h2 className="text-lg font-semibold text-green-800 flex items-center gap-2">
                  <CheckCircle className="h-5 w-5" />
                  Recently Paid ({recentPayouts.length})
                </h2>
                <p className="text-sm text-green-600 mt-1">Payouts created in the last 30 days</p>
              </div>
              {recentPayouts.length === 0 ? (
                <div className="p-8 text-center text-gray-500">No recent payouts</div>
              ) : (
                <div className="data-table-container">
                  <div className="data-table-scroll">
                    <table className="data-table">
                      <thead>
                        <tr>
                          <th>Recipient</th>
                          <th>Period</th>
                          <th className="text-right">Amount</th>
                          <th className="text-center">Status</th>
                          <th>Wire</th>
                          <th>Created</th>
                          <th className="text-right">Actions</th>
                        </tr>
                      </thead>
                      <tbody>
                        {recentPayouts.map((p) => {
                          const r = p.recipientId;
                          const company = r?.distributorData?.companyName || r?.supervisorData?.companyName;
                          return (
                            <tr key={p._id}>
                              <td className="cell-primary">
                                <div className="cell-stack">
                                  <span className="cell-stack-value">
                                    {r?.profile?.firstName} {r?.profile?.lastName}
                                  </span>
                                  {company && <span className="cell-stack-label">{company}</span>}
                                </div>
                              </td>
                              <td className="cell-secondary">
                                {formatShortDate(p.startDate)} – {formatShortDate(p.endDate)}
                              </td>
                              <td className="cell-mono text-right font-semibold">{formatCurrency(p.amount)}</td>
                              <td className="text-center">
                                <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${
                                  p.status === 'pending' ? 'bg-yellow-100 text-yellow-800' :
                                  p.status === 'sent' ? 'bg-blue-100 text-blue-800' :
                                  p.status === 'confirmed' ? 'bg-green-100 text-green-800' :
                                  'bg-gray-100 text-gray-700'
                                }`}>
                                  {p.status}
                                </span>
                              </td>
                              <td className="cell-mono">{p.wireNumber || '-'}</td>
                              <td className="cell-muted">{formatShortDate(p.createdAt)}</td>
                              <td className="text-right">
                                <div className="flex items-center justify-end gap-2">
                                  <button
                                    onClick={() => navigate('/admin/payouts')}
                                    className="p-1.5 text-gray-600 hover:bg-gray-100 rounded"
                                    title="Open in Payouts"
                                  >
                                    <Eye className="h-4 w-4" />
                                  </button>
                                  {p.status === 'pending' && (
                                    <button
                                      onClick={() => markSent(p._id)}
                                      className="p-1.5 text-blue-600 hover:bg-blue-50 rounded"
                                      title="Mark as Sent"
                                    >
                                      <Send className="h-4 w-4" />
                                    </button>
                                  )}
                                </div>
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Detail modal */}
        {selectedRow && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-hidden flex flex-col">
              <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between flex-shrink-0">
                <div>
                  <h2 className="text-xl font-semibold text-gray-900">{selectedRow.recipientName}</h2>
                  <p className="text-sm text-gray-500">
                    {selectedRow.recipientCompany || selectedRow.recipientEmail} · {roleLabel(selectedRow.recipientRole)}
                  </p>
                </div>
                <button onClick={() => setSelectedRow(null)} className="p-2 hover:bg-gray-100 rounded-full">
                  <X className="h-5 w-5 text-gray-500" />
                </button>
              </div>

              <div className="p-6 overflow-y-auto flex-1 min-h-0">
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
                  <div className="bg-gray-50 rounded-lg p-3">
                    <p className="text-xs text-gray-500">Owed</p>
                    <p className="text-lg font-bold text-gray-900">{formatCurrency(selectedRow.amount)}</p>
                  </div>
                  <div className="bg-gray-50 rounded-lg p-3">
                    <p className="text-xs text-gray-500">Membership</p>
                    <p className="text-lg font-bold text-gray-900">
                      {selectedRow.membershipEarnings > 0 ? formatCurrency(selectedRow.membershipEarnings) : '–'}
                    </p>
                  </div>
                  <div className="bg-gray-50 rounded-lg p-3">
                    <p className="text-xs text-gray-500">Orders</p>
                    <p className="text-lg font-bold text-gray-900">{selectedRow.orderCount}</p>
                  </div>
                  <div className="bg-gray-50 rounded-lg p-3">
                    <p className="text-xs text-gray-500">Invoices</p>
                    <p className="text-lg font-bold text-gray-900">{selectedRow.invoiceCount}</p>
                  </div>
                </div>

                {selectedRow.oldestUnpaidAt && (
                  <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 mb-6">
                    <p className="text-sm text-amber-900">
                      <strong>Oldest unpaid accrual:</strong> {formatShortDate(selectedRow.oldestUnpaidAt)}
                      {' '}({daysSince(selectedRow.oldestUnpaidAt, now)} days ago)
                    </p>
                  </div>
                )}

                <div className="mb-6">
                  <h3 className="text-sm font-semibold text-gray-700 mb-2">Aging Breakdown</h3>
                  <div className="border rounded-lg overflow-hidden">
                    <table className="w-full text-sm">
                      <thead className="bg-gray-50">
                        <tr>
                          <th className="px-3 py-2 text-left">Age</th>
                          <th className="px-3 py-2 text-right">Amount</th>
                        </tr>
                      </thead>
                      <tbody>
                        {BUCKETS.map(b => {
                          const v = selectedRow.aging[b.key as keyof Aging] || 0;
                          return (
                            <tr key={b.key} className="border-t">
                              <td className={`px-3 py-2 ${BUCKET_COLORS[b.key].text}`}>{b.label} days</td>
                              <td className={`px-3 py-2 text-right font-mono ${v > 0 ? BUCKET_COLORS[b.key].text : 'text-gray-300'}`}>
                                {v > 0 ? formatCurrency(v) : '–'}
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                      <tfoot className="bg-gray-50">
                        <tr>
                          <td className="px-3 py-2 font-semibold">Total Owed</td>
                          <td className="px-3 py-2 text-right font-mono font-bold text-indigo-700">
                            {formatCurrency(selectedRow.amount)}
                          </td>
                        </tr>
                      </tfoot>
                    </table>
                  </div>
                </div>

                {selectedRow.hasOverlappingPayout && (
                  <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-6">
                    <p className="text-sm text-red-900">
                      ⚠ This recipient has overlapping payouts in the current window — verify before drafting another.
                    </p>
                  </div>
                )}

                <div className="flex gap-3">
                  <button
                    onClick={() => navigate('/admin/payouts')}
                    className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
                  >
                    Draft Payout
                  </button>
                  <button
                    onClick={() => navigate(`/admin/payouts`)}
                    className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50"
                  >
                    View Payout History
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default AdminAP;
